import { Fragment } from "react";
import { getPointsBadge } from "@/lib/utils";
import { Loader } from "@/components/general/common/loader";

interface StatRow {
    stat: string;
    playerA: string | number;
    playerB: string | number;
    isTotal?: boolean;
}

interface ComparisonStatsMobileCardsProps {
    data: StatRow[];
    playerAName: string;
    playerBName: string;
    viewMode?: string;
    isLoading?: boolean;
}

const STAT_SHORT_NAMES: Record<string, string> = {
    "Defensive Points": "Defensive",
    "Offensive Points": "Offensive",
    "Team Play Points": "Team Play",
    "Effort Points": "Effort",
    "Bonus Points": "Bonus",
    "Total Points": "Total",
    "GSAx Points": "GSAx",
    "High Danger Shots": "High Danger",
    "Low Danger Shots": "Low Danger",
    "Lateral Pass Shots": "Lateral Pass",
    "Screened Shots": "Screened",
};

const shortStat = (name: string) => STAT_SHORT_NAMES[name] ?? name;

const formatValue = (v: string | number): string => {
    if (typeof v === "string") return v || "—";
    if (Math.abs(v) >= 10000) return `${(v / 1000).toFixed(0)}k`;
    if (Math.abs(v) >= 1000) return `${(v / 1000).toFixed(1)}k`;
    if (Math.abs(v) >= 100) return v.toFixed(1);
    return v % 1 === 0 ? v.toString() : v.toFixed(1);
};

const COLOR_A = "var(--color-chart-vivid-blue)";
const COLOR_B = "#ff9500";

export function ComparisonStatsMobileCards({
    data,
    playerAName,
    playerBName,
    viewMode,
    isLoading,
}: ComparisonStatsMobileCardsProps) {
    if (isLoading) {
        return (
            <div className="flex items-center justify-center min-h-[200px] bg-surface-container-lowest rounded-xl">
                <Loader message="Loading player statistics..." />
            </div>
        );
    }

    const noPlayersSelected = !playerAName && !playerBName;

    return (
        <div className="bg-surface-container-lowest rounded-xl overflow-hidden">
            {/*
             * CSS grid with 3 columns:
             *   col 1 = auto  → sized by the widest stat name, consistent across all rows
             *   col 2 = 1fr   → half the remaining space
             *   col 3 = 1fr   → half the remaining space
             */}
            <div className="grid grid-cols-[auto_1fr_1fr]">
                {/* Header — player names */}
                <div className="bg-surface-container-low border-b border-outline-variant/30 px-3 py-2" />
                <div className="bg-surface-container-low border-b border-outline-variant/30 px-2 py-2 text-center">
                    <span className="text-xs font-headline font-bold leading-tight" style={{ color: COLOR_A }}>
                        {playerAName || "Player A"}
                    </span>
                </div>
                <div className="bg-surface-container-low border-b border-outline-variant/30 px-2 py-2 text-center">
                    <span className="text-xs font-headline font-bold leading-tight" style={{ color: COLOR_B }}>
                        {playerBName || "Player B"}
                    </span>
                </div>

                {noPlayersSelected ? (
                    <div className="col-span-3 flex items-center justify-center py-10 text-on-surface-variant text-sm text-center px-4">
                        Select players above to compare their stats
                    </div>
                ) : (
                    data.map((row, idx) => {
                        const aVal = typeof row.playerA === "number" ? row.playerA : 0;
                        const bVal = typeof row.playerB === "number" ? row.playerB : 0;
                        const showBadges = row.isTotal && viewMode === "average";

                        const rowBg = row.isTotal
                            ? "bg-surface-container"
                            : idx % 2 === 0
                              ? "bg-surface-container-lowest"
                              : "bg-surface-container-low";

                        const borderTop = row.isTotal ? "border-t border-outline-variant/30" : "";

                        const renderValue = (val: string | number, numVal: number, color: string) => {
                            if (showBadges) {
                                if (numVal <= 0)
                                    return <span className="text-on-surface-variant font-bold text-sm">—</span>;
                                const badge = getPointsBadge(numVal);
                                return (
                                    <div
                                        className={`px-2 py-0.5 ${badge.bg} ${badge.text} rounded font-bold text-xs border ${badge.border} tabular-nums`}
                                    >
                                        {formatValue(numVal)}
                                    </div>
                                );
                            }
                            return (
                                <span
                                    className={`font-bold text-sm tabular-nums ${row.isTotal ? "text-primary" : ""}`}
                                    style={!row.isTotal ? { color } : undefined}
                                >
                                    {formatValue(val)}
                                </span>
                            );
                        };

                        return (
                            <Fragment key={row.stat}>
                                <div className={`px-3 py-2.5 flex items-center ${rowBg} ${borderTop}`}>
                                    <span className="text-xs font-medium text-on-surface-variant whitespace-nowrap">
                                        {shortStat(row.stat)}
                                    </span>
                                </div>
                                <div className={`px-2 py-2.5 flex items-center justify-center ${rowBg} ${borderTop}`}>
                                    {renderValue(row.playerA, aVal, COLOR_A)}
                                </div>
                                <div className={`px-2 py-2.5 flex items-center justify-center ${rowBg} ${borderTop}`}>
                                    {renderValue(row.playerB, bVal, COLOR_B)}
                                </div>
                            </Fragment>
                        );
                    })
                )}
            </div>
        </div>
    );
}
